import { useEffect, useState } from "react";
import { useParams, Link } from "react-router";
import { ArrowLeft, ChartColumn, ChevronDown, ChevronUp } from "lucide-react";
import { Bar as RBar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { surveyApi } from "@/lib/apiClient";
import AdminScreenTitle from "@/uniq/AdminScreenTitle";
import { T } from "@/uniq/theme";
import { QTYPE } from "@/uniq/questionMeta";
import { HEARING_QID, hearingOf } from "@shared-domain/survey/presets";
import { effectiveVisibleIds } from "@shared-domain/survey/logic";
import type { AnswerValue, Question, QuestionAggregate, SurveyResponse, SurveyResults as Results } from "@shared-domain/survey/contract";

// カテゴリカル系列色 (dataviz 検証済: slot1=ブランド teal・隣接CVD ΔE 16.2)。順序固定・循環しない。
const SERIES = ["#3DAEA3", "#2A78D6", "#EDA100", "#4A3AA7", "#E34948", "#EB6834", "#E87BA4", "#008300"];
// 未回答は系列色でなく中立グレー (状態であって選択肢ではない)。
const UNANSWERED = "#CBD5E1";
// NPS セグメント (推奨者=ブランド teal / 中立=amber / 批判者=red)。
const NPS_SEG = { promoter: "#3DAEA3", passive: "#EDA100", detractor: "#E34948" } as const;

/** scale の値分布用 ordinal teal ランプ (浅→深)。値の順序を色の濃さで表す。 */
const tealStep = (i: number, n: number): string => {
  const from = [124, 199, 191]; // #7CC7BF (白地 2:1 を確保する最浅)
  const to = [22, 101, 92]; // #16655C
  const t = n <= 1 ? 1 : i / (n - 1);
  const c = from.map((f, k) => Math.round(f + ((to[k] ?? 0) - f) * t));
  return `#${c.map((x) => x.toString(16).padStart(2, "0")).join("")}`;
};

interface Slice {
  name: string;
  value: number;
  color: string;
}

/** 円グラフ + 凡例 (件数/%)。value 0 の選択肢は凡例にのみ載せる。 */
function CountPie({ slices }: { slices: Slice[] }) {
  const total = slices.reduce((s, x) => s + x.value, 0);
  const drawn = slices.filter((s) => s.value > 0);
  if (total === 0) return <p style={{ fontSize: 13, color: T.faint, margin: 0 }}>回答なし</p>;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
      <div style={{ width: 150, height: 150, flex: "none" }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={drawn} dataKey="value" nameKey="name" innerRadius="50%" outerRadius="96%" stroke="#fff" strokeWidth={2} startAngle={90} endAngle={-270} isAnimationActive={false}>
              {drawn.map((s, i) => <Cell key={i} fill={s.color} />)}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 5, fontSize: 12.5, minWidth: 0 }}>
        {slices.map((s, i) => (
          <li key={i} style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: s.color, flex: "none" }} />
            <span style={{ color: T.ink, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</span>
            <span style={{ color: T.faint, flex: "none" }}>{s.value} ({total > 0 ? Math.round((s.value / total) * 100) : 0}%)</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Bar({ label, count, total }: { label: string; count: number; total: number }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
      <span style={{ width: 120, flex: "none", color: T.sub, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
      <div style={{ flex: 1, height: 16, background: "#F1F5F9", borderRadius: 6, overflow: "hidden" }}><div style={{ height: "100%", width: `${pct}%`, background: T.accent }} /></div>
      <span style={{ width: 64, flex: "none", textAlign: "right", color: T.faint }}>{count} ({pct}%)</span>
    </div>
  );
}

/** 設問カードの共通ヘッダー (タイプアイコン + ラベル + 回答 N/M)。 */
function CardHead({ q, type, answered, respondents }: { q?: Question; type: QuestionAggregate["type"]; answered: number; respondents: number }) {
  const meta = QTYPE[type];
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, margin: "0 0 12px" }}>
      <meta.Icon size={15} color={T.sub} style={{ flex: "none" }} />
      <p style={{ fontSize: 14, fontWeight: 700, color: T.ink, margin: 0, flex: 1, minWidth: 0 }}>{q?.label ?? ""}</p>
      <span style={{ flex: "none", fontSize: 11.5, color: T.faint }}>回答 {answered}/{respondents}</span>
    </div>
  );
}

/** 日付設問: 過去⇔未来の時間軸ヒストグラム。範囲 ≤62日は日単位 (欠損日 0 埋め)、超は月単位。 */
function DateHistogram({ dates }: { dates: string[] }) {
  const toDay = (s: string): string | null => {
    const m = s.trim().replace(/\//g, "-").match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    return m ? `${m[1]}-${m[2]!.padStart(2, "0")}-${m[3]!.padStart(2, "0")}` : null;
  };
  const days = dates.map(toDay).filter((d): d is string => d !== null).sort();
  if (days.length === 0) return <p style={{ fontSize: 13, color: T.faint, margin: 0 }}>回答なし</p>;
  const first = new Date(`${days[0]}T00:00:00Z`);
  const last = new Date(`${days[days.length - 1]}T00:00:00Z`);
  const spanDays = Math.round((last.getTime() - first.getTime()) / 86400000);
  const byKey = new Map<string, number>();
  const data: { x: string; count: number }[] = [];
  if (spanDays <= 62) {
    for (const d of days) byKey.set(d, (byKey.get(d) ?? 0) + 1);
    for (let t = first.getTime(); t <= last.getTime(); t += 86400000) {
      const key = new Date(t).toISOString().slice(0, 10);
      data.push({ x: key.slice(5).replace("-", "/"), count: byKey.get(key) ?? 0 });
    }
  } else {
    for (const d of days) { const k = d.slice(0, 7); byKey.set(k, (byKey.get(k) ?? 0) + 1); }
    const cur = new Date(`${days[0]!.slice(0, 7)}-01T00:00:00Z`);
    const end = days[days.length - 1]!.slice(0, 7);
    for (;;) {
      const key = cur.toISOString().slice(0, 7);
      data.push({ x: key.replace("-", "/"), count: byKey.get(key) ?? 0 });
      if (key >= end) break;
      cur.setUTCMonth(cur.getUTCMonth() + 1);
    }
  }
  return (
    <div style={{ width: "100%", height: 180 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -18 }}>
          <CartesianGrid vertical={false} stroke="#EEF2F7" />
          <XAxis dataKey="x" tick={{ fontSize: 10, fill: T.faint }} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} interval="preserveStartEnd" minTickGap={24} />
          <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: T.faint }} tickLine={false} axisLine={false} />
          <Tooltip cursor={{ fill: "rgba(15,23,42,.05)" }} formatter={(v) => [`${v} 件`, "回答数"]} />
          <RBar dataKey="count" fill={T.accent} radius={[3, 3, 0, 0]} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/** チェックボックス設問: 既定は回答数降順・トグルで選択肢順に戻せる。 */
function CheckboxBody({ agg }: { agg: Extract<QuestionAggregate, { type: "checkbox" }> }) {
  const [byCount, setByCount] = useState(true);
  const rows = byCount ? [...agg.counts].sort((a, b) => b.count - a.count) : agg.counts;
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <button onClick={() => setByCount((v) => !v)}
          style={{ fontSize: 11, fontWeight: 600, color: T.sub, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 9999, padding: "3px 10px", cursor: "pointer" }}>
          {byCount ? "選択肢順で表示" : "回答数順で表示"}
        </button>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {rows.map((c) => <Bar key={c.v} label={c.label} count={c.count} total={agg.total} />)}
      </div>
    </div>
  );
}

/** テキスト設問: 件数 + 折りたたみ (既定は閉・HearingCard と同じ開閉パターン)。 */
function TextBody({ answers }: { answers: { customerName: string; text: string }[] }) {
  const [open, setOpen] = useState(false);
  if (answers.length === 0) return <p style={{ fontSize: 13, color: T.faint, margin: 0 }}>回答なし</p>;
  return (
    <div>
      <button onClick={() => setOpen((v) => !v)} aria-expanded={open}
        style={{ display: "flex", alignItems: "center", gap: 8, background: "transparent", border: 0, padding: 0, cursor: "pointer" }}>
        <span style={{ fontSize: 15, fontWeight: 800, color: T.ink }}>{answers.length}<span style={{ fontSize: 12, fontWeight: 600, color: T.sub, marginLeft: 2 }}>件</span></span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 12, color: T.accent, fontWeight: 600 }}>
          {open ? "閉じる" : "回答を表示"}{open ? <ChevronUp size={14} color={T.accent} /> : <ChevronDown size={14} color={T.accent} />}
        </span>
      </button>
      {open && (
        <ul style={{ margin: "10px 0 0", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6, fontSize: 13 }}>
          {answers.map((a, i) => <li key={i} style={{ background: "#F8FAFC", borderRadius: 8, padding: "8px 10px" }}><span style={{ color: T.faint }}>{a.customerName}: </span>{a.text}</li>)}
        </ul>
      )}
    </div>
  );
}

/** scale の NPS セグメント積み上げバー (推奨者 9-10 / 中立 7-8 / 批判者 0-6)。 */
function NpsSegments({ agg }: { agg: Extract<QuestionAggregate, { type: "scale" }> }) {
  let promoter = 0, passive = 0, detractor = 0;
  for (const d of agg.distribution) {
    if (d.value >= 9) promoter += d.count;
    else if (d.value >= 7) passive += d.count;
    else detractor += d.count;
  }
  const total = promoter + passive + detractor;
  if (total === 0) return null;
  const segs = [
    { label: "推奨者(9-10)", n: promoter, color: NPS_SEG.promoter },
    { label: "中立(7-8)", n: passive, color: NPS_SEG.passive },
    { label: "批判者(0-6)", n: detractor, color: NPS_SEG.detractor },
  ];
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ display: "flex", height: 16, borderRadius: 9999, overflow: "hidden", gap: 2, background: "#F1F5F9" }}>
        {segs.filter((s) => s.n > 0).map((s) => <div key={s.label} title={`${s.label} ${s.n}`} style={{ width: `${(s.n / total) * 100}%`, background: s.color }} />)}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "3px 14px", marginTop: 6, fontSize: 11.5 }}>
        {segs.map((s) => (
          <span key={s.label} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 9, height: 9, borderRadius: 3, background: s.color }} />
            <span style={{ color: T.sub }}>{s.label}</span>
            <span style={{ color: T.faint }}>{s.n} ({Math.round((s.n / total) * 100)}%)</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function Card({ agg, q, respondents }: { agg: QuestionAggregate; q?: Question; respondents: number }) {
  const answered =
    agg.type === "radio" || agg.type === "checkbox" ? agg.total
    : agg.type === "scale" ? agg.count
    : agg.answers.length;
  // 未回答スライス (任意/必須問わず。音声モードの必須緩和で必須設問も未回答があり得る)。
  const unanswered = Math.max(0, respondents - answered);
  return (
    <div style={{ background: "#fff", borderRadius: 12, padding: 16 }}>
      <CardHead q={q} type={agg.type} answered={answered} respondents={respondents} />
      {agg.type === "radio" && (
        <CountPie slices={[
          ...agg.counts.map((c, i) => ({ name: c.label, value: c.count, color: SERIES[i % SERIES.length]! })),
          ...(unanswered > 0 ? [{ name: "未回答", value: unanswered, color: UNANSWERED }] : []),
        ]} />
      )}
      {agg.type === "checkbox" && <CheckboxBody agg={agg} />}
      {agg.type === "text" && <TextBody answers={agg.answers} />}
      {agg.type === "date" && <DateHistogram dates={agg.answers.map((a) => a.date)} />}
      {agg.type === "scale" && (
        <div>
          <p style={{ fontSize: 13, color: T.sub, margin: "0 0 10px" }}>平均 <b style={{ color: T.ink }}>{agg.average?.toFixed(2) ?? "-"}</b>{typeof agg.nps === "number" ? <> ・ NPS <b style={{ color: T.ink }}>{agg.nps}</b></> : null}</p>
          <CountPie slices={[
            ...agg.distribution.map((d, i) => ({ name: String(d.value), value: d.count, color: tealStep(i, agg.distribution.length) })),
            ...(unanswered > 0 ? [{ name: "未回答", value: unanswered, color: UNANSWERED }] : []),
          ]} />
          {typeof agg.nps === "number" && <NpsSegments agg={agg} />}
        </div>
      )}
    </div>
  );
}

/**
 * ヒアリング対象カード (PRODUCT_DEV-574): 人数だけ表示し、クリックで
 * 顧客名×ヒアリング相手(記名)×担当×日時の統合表を展開する。
 * 旧「回答者一覧（記名）」テーブルはこの統合表に集約した。
 */
function HearingCard({ q, responses, respondents }: { q?: Question; responses: SurveyResponse[]; respondents: number }) {
  const [open, setOpen] = useState(false);
  const count = responses.filter((r) => hearingOf(r).trim() !== "").length;
  return (
    <div style={{ background: "#fff", borderRadius: 12, padding: 16 }}>
      <CardHead q={q} type="text" answered={count} respondents={respondents} />
      <button onClick={() => setOpen((v) => !v)} aria-expanded={open}
        style={{ display: "flex", alignItems: "center", gap: 8, background: "transparent", border: 0, padding: 0, cursor: "pointer" }}>
        <span style={{ fontSize: 15, fontWeight: 800, color: T.ink }}>{count}<span style={{ fontSize: 12, fontWeight: 600, color: T.sub, marginLeft: 2 }}>名</span></span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 12, color: T.accent, fontWeight: 600 }}>
          {open ? "閉じる" : "一覧を表示"}{open ? <ChevronUp size={14} color={T.accent} /> : <ChevronDown size={14} color={T.accent} />}
        </span>
      </button>
      {open && (
        <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse", marginTop: 10 }}>
          <thead><tr style={{ color: T.faint, textAlign: "left" }}><th style={{ padding: 4 }}>顧客名</th><th style={{ padding: 4 }}>ヒアリング相手（記名）</th><th style={{ padding: 4 }}>担当</th><th style={{ padding: 4 }}>日時</th></tr></thead>
          <tbody>
            {responses.map((r) => (
              <tr key={r.customerId} style={{ borderTop: "1px solid #F1F5F9" }}>
                <td style={{ padding: 4 }}>{r.customerName || r.customerId}</td>
                <td style={{ padding: 4 }}>{hearingOf(r).trim() || "—"}</td>
                <td style={{ padding: 4 }}>{r.submittedByName}</td>
                <td style={{ padding: 4, color: T.faint }}>{r.submittedAt.slice(0, 16).replace("T", " ")}</td>
              </tr>
            ))}
            {responses.length === 0 && <tr><td colSpan={4} style={{ padding: 8, color: T.faint }}>まだ回答がありません。</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** 冒頭サマリー: 回答率ドーナツ (指名で未回答があるときのみ) + 回答数/未回答の数値。 */
function SummaryCard({ responseCount, unansweredCount }: { responseCount: number; unansweredCount: number }) {
  const assigned = responseCount + unansweredCount;
  const rate = assigned > 0 ? Math.round((responseCount / assigned) * 100) : 0;
  return (
    <div style={{ background: "#fff", borderRadius: 12, padding: 16, display: "flex", alignItems: "center", gap: 18, marginBottom: 12 }}>
      {unansweredCount > 0 && (
        <div style={{ position: "relative", width: 92, height: 92, flex: "none" }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={[{ name: "回答済", value: responseCount }, { name: "未回答", value: unansweredCount }]} dataKey="value" innerRadius="68%" outerRadius="100%" stroke="#fff" strokeWidth={2} startAngle={90} endAngle={-270} isAnimationActive={false}>
                <Cell fill={T.accent} />
                <Cell fill={UNANSWERED} />
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 800, color: T.ink }}>{rate}%</span>
        </div>
      )}
      <div style={{ display: "flex", gap: 24 }}>
        <div>
          <div style={{ fontSize: 11, color: T.faint }}>回答数</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: T.ink }}>{responseCount}</div>
        </div>
        {unansweredCount > 0 && (
          <div>
            <div style={{ fontSize: 11, color: T.faint }}>未回答</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#B45309" }}>{unansweredCount}</div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function SurveyResults() {
  const { surveyId } = useParams();
  const [results, setResults] = useState<Results | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!surveyId) return;
    surveyApi.results(surveyId).then((r) => setResults(r.results)).catch((e) => setError(String(e)));
  }, [surveyId]);

  if (error) return <div style={{ padding: 24, color: "#dc2626" }}>{error}</div>;
  if (!results) return <div style={{ padding: 24, color: T.faint }}>読み込み中…</div>;
  const qById = new Map(results.survey.questions.map((q) => [q.questionId, q]));
  // 設問ごとの対象者数 (表示条件を尊重)。分岐で非表示だった回答者を「未回答」に数えない。
  const eligibleByQ = new Map<string, number>();
  for (const r of results.responses) {
    const byId = new Map<string, AnswerValue>(r.answers.map((a) => [a.questionId, a.value]));
    for (const qid of effectiveVisibleIds(results.survey.questions, byId))
      eligibleByQ.set(qid, (eligibleByQ.get(qid) ?? 0) + 1);
  }

  return (
    <div style={{ minHeight: "100vh", background: T.bg }}>
      <div style={{ background: "#fff", borderBottom: `1px solid ${T.line}` }}>
        <div style={{ maxWidth: 800, margin: "0 auto", padding: "14px 16px", display: "flex", alignItems: "center", gap: 10 }}>
          <Link to="/admin" style={{ display: "flex", alignItems: "center", gap: 4, color: T.sub, textDecoration: "none", fontSize: 13 }}><ArrowLeft size={16} />一覧</Link>
        </div>
      </div>
      <div style={{ maxWidth: 800, margin: "0 auto", padding: 16 }}>
        <AdminScreenTitle icon={ChartColumn} title="アンケート集計" surveyName={results.survey.title} />
        <SummaryCard responseCount={results.responseCount} unansweredCount={results.unanswered.length} />
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {results.perQuestion.map((agg) =>
            agg.questionId === HEARING_QID
              ? <HearingCard key={agg.questionId} q={qById.get(agg.questionId)} responses={results.responses} respondents={eligibleByQ.get(agg.questionId) ?? 0} />
              : <Card key={agg.questionId} agg={agg} q={qById.get(agg.questionId)} respondents={eligibleByQ.get(agg.questionId) ?? 0} />,
          )}
        </div>
        {results.unanswered.length > 0 && (
          <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 12, padding: 16, marginTop: 12 }}>
            <h2 style={{ fontSize: 14, fontWeight: 700, color: "#92400E", marginTop: 0 }}>未回答（指名）</h2>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: "#92400E" }}>
              {results.unanswered.map((u) => <li key={`${u.repUserId}#${u.customerId}`}>{u.customerName || u.customerId} / 担当: {u.repUserName || u.repUserId}</li>)}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
